import { Avatar, AvatarFallbackWithInitials, Button } from 'components'
import { AlertTriangle, ImageIcon, Upload, X } from 'lucide-react'
import { AttachmentEntity } from 'openapi'
import { ChangeEvent, useEffect, useRef, useState } from 'react'
import { useImageUpload } from '../hooks'
import styles from './PictureUpload.module.css'

interface PictureUploadProps {
  currentImageUrl?: string
  onUploadComplete: (file: AttachmentEntity) => Promise<void>
  onClear: () => void
  className?: string
  name?: string
  id?: string
  allowClear?: boolean
  noImageFallback?: React.ReactNode
  size?: 'sm' | 'md' | 'lg' | 'xl'
}

export const PictureUpload = ({
  currentImageUrl,
  onUploadComplete,
  onClear,
  className = '',
  name,
  id,
  allowClear = true,
  noImageFallback,
  size = 'lg',
}: PictureUploadProps) => {
  const fileInputRef = useRef<HTMLInputElement>(null)
  const [isHovering, setIsHovering] = useState(false)
  const [previewUrl, setPreviewUrl] = useState<string | undefined>(currentImageUrl)
  const { handleUpload, isUploading, error } = useImageUpload()

  const sizeClasses = {
    sm: 'h-20 w-20',
    md: 'h-24 w-24',
    lg: 'h-30 w-30',
    xl: 'h-32 w-32',
  }

  const prevImageUrl = useRef(currentImageUrl)
  useEffect(() => {
    if (currentImageUrl !== prevImageUrl.current) {
      setPreviewUrl(currentImageUrl)
      prevImageUrl.current = currentImageUrl
    }
  }, [currentImageUrl])

  const handleFileChange = async (event: ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0]
    if (!file) return

    try {
      // Show preview immediately
      const tempPreviewUrl = URL.createObjectURL(file)
      setPreviewUrl(tempPreviewUrl)

      // Upload the file
      const result = await handleUpload(file)

      // Clean up temporary preview URL
      URL.revokeObjectURL(tempPreviewUrl)

      // Update the entity with the new file ID
      await onUploadComplete(result)

      // Set the actual uploaded image URL
      setPreviewUrl(result.publicUrl)
    } catch {
      // Revert to previous image on error
      setPreviewUrl(currentImageUrl)
    }
  }

  const handleClear = (e: React.MouseEvent) => {
    e.stopPropagation()
    setPreviewUrl(undefined)
    onClear()
  }

  return (
    <div
      className={`relative flex flex-col items-center space-y-2 ${className} ${styles.container}`}
    >
      <div className={`relative ${sizeClasses[size]}`}>
        <div
          className="relative flex h-full w-full cursor-pointer items-center justify-center overflow-hidden rounded-full"
          onMouseEnter={() => setIsHovering(true)}
          onMouseLeave={() => setIsHovering(false)}
          onClick={() => fileInputRef.current?.click()}
          role="button"
          tabIndex={0}
          aria-label="Upload picture"
        >
          {previewUrl ? (
            <Avatar size="lg">
              <img src={previewUrl} alt="Picture" className="h-full w-full object-cover" />
            </Avatar>
          ) : noImageFallback ? (
            noImageFallback
          ) : name && id ? (
            <AvatarFallbackWithInitials name={name} id={id} size="lg" />
          ) : (
            <ImageIcon size={48} className="text-gray-500" />
          )}

          {(isHovering || isUploading) && (
            <div className="absolute inset-0 flex items-center justify-center transition-opacity">
              {isUploading ? (
                <div className="h-10 w-10 animate-spin rounded-full border-2 border-white bg-black/20 p-2" />
              ) : (
                <Upload size={24} className="rounded-full bg-black/20 p-1 text-white" />
              )}
            </div>
          )}

          {error && (
            <div className="absolute inset-0 flex items-center justify-center bg-black/20 transition-opacity">
              <div className="text-white">
                <AlertTriangle size={24} />
              </div>
            </div>
          )}

          <input
            ref={fileInputRef}
            type="file"
            className="hidden"
            accept="image/*"
            onChange={handleFileChange}
            aria-label="Upload picture"
          />
        </div>
        {previewUrl && allowClear && (
          <Button
            className={`absolute -top-3 -right-3 size-6 rounded-full bg-black/10 text-white transition-all duration-200 hover:bg-black/70 ${styles.removeButton}`}
            onClick={handleClear}
            aria-label="Remove image"
          >
            <X size={10} />
          </Button>
        )}
      </div>
    </div>
  )
}
